// Story 7.1 — TanStack Query hook wrapping GET /api/v1/legal/current-tos.
//
// Story 1.14 backend served the Terms-of-Service history + accept-current
// flow but the SPA had no consumer of the "current version" query until
// now. The create-account form needs the current version_label so it
// can send it in the register payload — the backend REJECTS register
// without a valid tos_version_label matching the currently-active row.
//
// 5-minute staleTime: ToS versions change rarely and the fetch is
// side-effect free.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

/** Story 1.33 — the three published documents. Mirrors `DocumentKind` in
 *  server/src/domain/legal_document.rs. */
export type LegalDocumentKind = "user" | "creator_program" | "privacy";

export interface CurrentTos {
  id: string;
  version_label: string;
  body_md: string;
  summary: string;
  effective_at: string;
  /** Which document this is. Two documents can now legitimately share a
   *  version_label, so the label alone no longer identifies a response. */
  document_kind: LegalDocumentKind;
}

export interface CurrentTosError {
  message: string;
  status: number;
}

async function fetchCurrentTos(kind: LegalDocumentKind): Promise<CurrentTos> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/legal/current-tos?kind=${kind}`, {
      credentials: "include",
    });
  } catch (networkErr) {
    // Backend unreachable — Vite proxy timeout, backend not running, etc.
    const err: CurrentTosError = {
      message:
        networkErr instanceof Error
          ? networkErr.message
          : "Network error contacting backend.",
      status: 0,
    };
    throw err;
  }
  attachRequestIdToScope(response);
  if (!response.ok) {
    const err: CurrentTosError = {
      message: `current-tos fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    throw err;
  }
  return (await response.json()) as CurrentTos;
}

/** Story 1.33 — `kind` defaults to the user terms, which is what every
 *  existing caller meant when there was only one document.
 *
 *  THE QUERY KEY MUST CARRY THE KIND. Two consumers now fetch two different
 *  documents through this one hook; sharing a cache entry between them would
 *  serve the Creator Program terms to the signup form, or the user terms to
 *  the creator application — and the caller would send the wrong id back as
 *  its record of consent. */
export function useCurrentTos(kind: LegalDocumentKind = "user") {
  return useQuery<CurrentTos, CurrentTosError>({
    queryKey: ["legal-current-tos", kind],
    queryFn: () => fetchCurrentTos(kind),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
